import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, CalendarDays, ChevronRight, CreditCard, GraduationCap, LifeBuoy, MessagesSquare } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatMoneyCompact } from '../../utils/datetime';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';

// Shortcuts to the areas the signed-in role can open (`nav` is the role matrix key), so a link
// never lands a call-center login on a page it may not use.
const ACTIONS = [
  { key: 'courses', nav: 'courses', to: '/courses', Icon: BookOpen, tone: 'brand' },
  { key: 'students', nav: 'students', to: '/students', Icon: GraduationCap, tone: 'info' },
  { key: 'sales', nav: 'sales', to: '/sales', Icon: CreditCard, tone: 'success' },
  { key: 'inbox', nav: 'inbox', to: '/inbox', Icon: MessagesSquare, tone: 'purple' },
  { key: 'support', nav: 'support', to: '/support', Icon: LifeBuoy, tone: 'teal' },
  { key: 'calendar', nav: 'calendar', to: '/calendar', Icon: CalendarDays, tone: 'coral' },
];

export function QuickActions() {
  const { t } = useI18n();
  const { canSeeNav } = usePermissions();
  const items = ACTIONS.filter((action) => canSeeNav(action.nav));
  if (items.length === 0) return null;

  return (
    <section className="section-card dash-quick" aria-labelledby="dash-quick-title">
      <div className="section-head">
        <h2 className="section-head__title" id="dash-quick-title">{t('dashboard.quickActions.title')}</h2>
      </div>
      <div className="dash-list">
        {items.map(({ key, to, Icon, tone }) => (
          <Link key={key} to={to} className="ui-list-row">
            <span className="ui-icon-chip ui-icon-chip--md" data-tone={tone}>
              <Icon aria-hidden="true" />
            </span>
            <span className="ui-list-row__body">
              <span className="ui-list-row__title">{t(`dashboard.quickActions.${key}.label`)}</span>
              <span className="ui-list-row__sub">{t(`dashboard.quickActions.${key}.desc`)}</span>
            </span>
            <ChevronRight className="ui-list-row__chevron" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </section>
  );
}

/** The courses that earned the most, each with a bar scaled to the best one. Money only. */
export function TopCourses({ courses, loading = false }) {
  const { t } = useI18n();
  const best = Math.max(1, ...courses.map((course) => course.revenue));

  return (
    <section className="section-card dash-top" aria-labelledby="dash-top-title" aria-busy={loading}>
      <div className="section-head">
        <div>
          <h2 className="section-head__title" id="dash-top-title">{t('dashboard.topCourses')}</h2>
          <p className="section-head__desc">{t('dashboard.topCoursesDesc')}</p>
        </div>
        <Link to="/courses" className="section-head__action">
          {t('dashboard.viewAll')}
          <ArrowRight className="dash-action-arrow" size={14} aria-hidden="true" />
        </Link>
      </div>
      {loading ? (
        <div className="dash-top__skeleton" aria-hidden="true">
          {[0, 1, 2].map((i) => <div key={i} className="ui-skel dash-top__skel" />)}
        </div>
      ) : courses.length === 0 ? (
        <InlineEmpty kind="empty" title={t('dashboard.noRevenueData')} />
      ) : (
        <ol className="dash-list dash-top__list">
          {courses.map((course, index) => (
            <li key={course.id}>
              <Link to={`/courses/edit/${course.id}`} className="ui-list-row dash-top__row">
                <span className="dash-top__rank">{index + 1}</span>
                <span className="dash-top__body">
                  <span className="dash-top__title" title={course.title}>{course.title}</span>
                  <span className="dash-top__meter">
                    <span className="dash-top__bar" aria-hidden="true">
                      <span style={{ width: `${Math.max(6, (course.revenue / best) * 100)}%` }} />
                    </span>
                    <span className="dash-top__revenue">{formatMoneyCompact(course.revenue)}</span>
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
